import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Truck, MapPin, DollarSign, Plus, Edit, Trash2, CheckCircle2 } from 'lucide-react';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
interface ShippingZone {
  id: string;
  name: string;
  regions: string[];
  fee: number;
  estimatedDays: string;
}
export function VendorShippingScreen() {
  const [zones, setZones] = useState<ShippingZone[]>([{
    id: 'Z1',
    name: 'Lagos Mainland',
    regions: ['Ikeja', 'Yaba', 'Surulere', 'Maryland'],
    fee: 2000,
    estimatedDays: '1-2'
  }, {
    id: 'Z2',
    name: 'Lagos Island',
    regions: ['Victoria Island', 'Lekki', 'Ikoyi'],
    fee: 2500,
    estimatedDays: '1-2'
  }, {
    id: 'Z3',
    name: 'Other States',
    regions: ['Abuja', 'Port Harcourt', 'Ibadan', 'Kano'],
    fee: 5000,
    estimatedDays: '3-5'
  }]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState('50000');
  const [showSuccess, setShowSuccess] = useState(false);
  const handleSave = () => {
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };
  const handleDeleteZone = (zoneId: string) => {
    if (confirm('Are you sure you want to delete this shipping zone?')) {
      setZones(zones.filter(z => z.id !== zoneId));
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-2315">
      <Header title="Shipping Settings" data-id="element-2316" />

      <div className="p-4 space-y-4" data-id="element-2317">
        {/* Free Shipping Threshold */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-2318">
          <h3 className="font-bold text-gray-900 mb-3" data-id="element-2319">Free Shipping</h3>
          <Input label="Minimum Order Amount (₦)" type="number" placeholder="0" value={freeShippingThreshold} onChange={e => setFreeShippingThreshold(e.target.value)} icon={<DollarSign className="w-4 h-4" data-id="element-2321" />} data-id="element-2320" />
          <p className="text-xs text-gray-500 mt-2" data-id="element-2322">
            Orders above this amount get free shipping
          </p>
        </div>

        {/* Shipping Zones */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-2323">
          <div className="flex items-center justify-between mb-3" data-id="element-2324">
            <h3 className="font-bold text-gray-900" data-id="element-2325">Shipping Zones</h3>
            <button className="text-sm font-medium text-primary flex items-center gap-1" data-id="element-2326">
              <Plus className="w-4 h-4" data-id="element-2327" />
              Add Zone
            </button>
          </div>

          <div className="space-y-3" data-id="element-2328">
            {zones.map((zone, idx) => <motion.div key={zone.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.05
          }} className="p-4 bg-gray-50 rounded-xl" data-id="element-2329">
                <div className="flex items-start justify-between mb-3" data-id="element-2330">
                  <div className="flex-1" data-id="element-2331">
                    <h4 className="font-medium text-gray-900 mb-1" data-id="element-2332">
                      {zone.name}
                    </h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2" data-id="element-2333">
                      <MapPin className="w-4 h-4" data-id="element-2334" />
                      <span data-id="element-2335">{zone.regions.join(', ')}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm" data-id="element-2336">
                      <div className="flex items-center gap-1" data-id="element-2337">
                        <DollarSign className="w-4 h-4 text-gray-400" data-id="element-2338" />
                        <span className="font-medium text-gray-900" data-id="element-2339">
                          ₦{zone.fee.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1" data-id="element-2340">
                        <Truck className="w-4 h-4 text-gray-400" data-id="element-2341" />
                        <span className="text-gray-600" data-id="element-2342">
                          {zone.estimatedDays} days
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-2343">
                    <button className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-primary" data-id="element-2344">
                      <Edit className="w-4 h-4" data-id="element-2345" />
                    </button>
                    <button onClick={() => handleDeleteZone(zone.id)} className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-error" data-id="element-2346">
                      <Trash2 className="w-4 h-4" data-id="element-2347" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Logistics Partners */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-50" data-id="element-2348">
          <h3 className="font-bold text-gray-900 mb-3" data-id="element-2349">Logistics Partners</h3>
          <div className="space-y-2" data-id="element-2350">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl" data-id="element-2351">
              <div className="flex items-center gap-3" data-id="element-2352">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center" data-id="element-2353">
                  <Truck className="w-5 h-5 text-primary" data-id="element-2354" />
                </div>
                <div data-id="element-2355">
                  <p className="font-medium text-gray-900" data-id="element-2356">
                    Fast Motion Logistics
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-2357">Connected</p>
                </div>
              </div>
              <div className="w-5 h-5 bg-success rounded-full flex items-center justify-center" data-id="element-2358">
                <CheckCircle2 className="w-3 h-3 text-white" data-id="element-2359" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-40" data-id="element-2360">
        <div className="max-w-md mx-auto" data-id="element-2361">
          <Button fullWidth size="lg" onClick={handleSave} data-id="element-2362">
            Save Changes
          </Button>
        </div>
      </div>

      {/* Success Toast */}
      {showSuccess && <motion.div initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-2363">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-2364">
            <CheckCircle2 className="w-5 h-5 text-success" data-id="element-2365" />
            <span className="text-sm font-medium" data-id="element-2366">
              Settings saved successfully
            </span>
          </div>
        </motion.div>}
    </motion.div>;
}